import { EarlyAccessIndicator, Typography } from 'Components/atoms';
import { Form, Image } from 'react-bootstrap';
import { INTEGRATIONHEADER, INTEGRATIONSTATUSLABEL, NEVER } from 'Core-utils/constants/constants';
import { ConnectorStatus } from '@ampsec/platform-client/src/dto';
import prettyMilliseconds from 'pretty-ms';
import moment from 'moment';
import './styles.scss';
import { SETTINGS_SPECIFIC_ROUTE } from 'Core-utils/routes';
import { Link } from 'react-router-dom';
import { useI18n } from 'Hooks/useI18n';
import { ASSET_ATTRIBUTION_SOURCE_BADGE_LABEL } from 'Components/pages/SettingsPage/assetAttributionSource';

interface IntegrationHeaderProps {
    icon: string;
    label: string;
    updatedTime: string;
    syncTime: string;
    status: ConnectorStatus;
    checked?: boolean;
    onToggle?: () => void;
    providerCategories?: string[];
    note?: string;
    isAttributionSource?: boolean;
    isEarlyAccess?: boolean;
}
const IntegrationHeaderContent = ({
    icon,
    label,
    updatedTime,
    status,
    syncTime,
    providerCategories,
    note,
    isAttributionSource,
    isEarlyAccess,
}: IntegrationHeaderProps) => {
    const syncTimeText = !isNaN(parseInt(syncTime ?? ''))
        ? `(${INTEGRATIONHEADER[3]} ${prettyMilliseconds(parseInt(syncTime) * 1000)})`
        : `(${NEVER})`;
    const { t } = useI18n();

    return (
        <div className="d-flex flex-row gap-3">
            <Image src={icon} className="integration-header__icon" />
            <div className="d-flex flex-column gap-2">
                <div className="integration-header__label-row d-flex align-items-center gap-2">
                    <Typography variant="subtitle5" color="text-high-emphasis" className="m-0">
                        {label}
                    </Typography>
                    {isEarlyAccess && <EarlyAccessIndicator />}
                </div>
                {isAttributionSource && (
                    <span className="integration-header__attribution-badge">{ASSET_ATTRIBUTION_SOURCE_BADGE_LABEL}</span>
                )}
                <div className="text-uppercase d-flex flex-row align-items-center gap-2">
                    <Typography variant="body3" color="text-medium-emphasis" className="m-0">
                        {INTEGRATIONHEADER[0]}
                        {INTEGRATIONSTATUSLABEL[status]}
                    </Typography>
                    <Typography variant="body5" color="text-medium-emphasis" className="gap-1">
                        {status}
                    </Typography>
                    <div className={`integration-header__circle ${status}`}></div>
                </div>
                <div className="d-flex flex-row align-items-center gap-1">
                    <Typography variant="subtitle3" color="text-medium-emphasis">
                        {INTEGRATIONHEADER[1]}
                    </Typography>
                    <Typography variant="body5" color="text-medium-emphasis" className="gap-1">
                        {updatedTime ? moment(updatedTime).fromNow() : NEVER}
                    </Typography>
                    <Typography variant="subtitle3" color="text-medium-emphasis">
                        {syncTimeText}
                    </Typography>
                </div>
                {providerCategories?.includes('NOTIFICATION') && note && (
                    <Typography variant="subtitle3" color="text-medium-emphasis" className="mt-1">
                        <strong>NOTE</strong>: {note}{' '}
                        <Link
                            to={SETTINGS_SPECIFIC_ROUTE}
                            style={{ color: 'var(--primary-color)', textDecoration: 'underline' }}
                        >
                           {t('COLLABORATION_CONNECTOR_DISABLED_NOTE_LINK_TEXT')}
                        </Link>
                    </Typography>
                )}
            </div>
        </div>
    );
};

const IntegrationHeader = (props: IntegrationHeaderProps) => {
    const { icon, label, updatedTime, syncTime, checked, status, onToggle, providerCategories, note, isAttributionSource, isEarlyAccess } = props;

    return (
        <div className="integration-header w-100 d-flex flex-row justify-content-between align-items-center bg-text-black border border-structural-dark-stroke-400">
            <IntegrationHeaderContent
                icon={icon}
                label={label}
                updatedTime={updatedTime}
                status={status}
                syncTime={syncTime}
                providerCategories={providerCategories}
                note={note}
                isAttributionSource={isAttributionSource}
                isEarlyAccess={isEarlyAccess}
            />
            <Form.Check type="switch" checked={checked} onChange={onToggle} data-testid="checkbox" />
        </div>
    );
};

export default IntegrationHeader;
